import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, AlertTriangle, Star, Heart } from 'lucide-react';
import { getVisitorId } from '../utils/visitor';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const ClinicProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchClinicProfile = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch clinic profile with all doctors using the public endpoint
        const profileResponse = await fetch(`${API_URL}/clinics/${id}/profile`);

        if (!profileResponse.ok) {
          throw new Error('Clinic not found');
        }

        const profileData = await profileResponse.json();
        setClinic(profileData.clinic);
        setDoctors(profileData.doctors || []);
      } catch (err) {
        console.error('Error fetching clinic profile:', err);
        setError(err.message || 'Failed to load clinic profile');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchClinicProfile();
    }
  }, [id]);

  // Track page view for analytics
  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Get or create visitor ID for unique visitor tracking
        const visitorId = getVisitorId();

        await fetch(`${API_URL}/clinics/${id}/track-view`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ visitorId }),
        });
      } catch (err) {
        // Silently fail - don't interrupt user experience
        console.log('Failed to track page view:', err);
      }
    };

    if (id) {
      trackPageView();
    }
  }, [id]);

  const handleDoctorClick = (doctor) => {
    // Navigate to doctor profile page which will open the booking flow
    navigate(`/doctor/${doctor.id}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-obsidian flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-light-text dark:text-pure-white mx-auto mb-4" />
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading clinic...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !clinic) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-obsidian flex items-center justify-center p-4">
        <div className="glass border border-light-border dark:border-border rounded-2xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="font-display text-2xl italic tracking-tighter mb-2">
            Clinic Not Found
          </h2>
          <p className="text-light-text-secondary dark:text-silver/60 mb-6">
            {error || 'The clinic you are looking for could not be found.'}
          </p>
          <button
            onClick={() => navigate('/marketplace')}
            className="btn-primary w-full"
          >
            Browse Marketplace
          </button>
        </div>
      </div>
    );
  }

  // Show different views based on display mode
  const showAllDoctors = clinic.displayMode === 'all_doctors';
  // Respect showDoctors visibility toggle (defaults to true if not set)
  const shouldShowDoctors = clinic.showDoctors !== false;
  // Respect showReviews visibility toggle (defaults to true if not set)
  const shouldShowReviews = clinic.showReviews !== false;

  // Calculate aggregated reviews data
  const totalReviews = doctors.reduce((sum, doctor) => sum + (doctor.reviewCount || 0), 0);
  const averageRating = doctors.length > 0
    ? doctors.reduce((sum, doctor) => sum + (doctor.averageRating || 0) * (doctor.reviewCount || 0), 0) / totalReviews
    : 0;

  return (
    <div className="min-h-screen bg-light-bg dark:bg-obsidian px-4 py-8">
      <Helmet>
        <title>{clinic.name} - Calendiary</title>
        <meta name="description" content={clinic.linkInBioBio || clinic.description || `Book an appointment with ${clinic.name}`} />

        {/* Open Graph tags for social media sharing */}
        <meta property="og:title" content={clinic.name} />
        <meta property="og:description" content={clinic.linkInBioBio || clinic.description || `Book an appointment with ${clinic.name}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {clinic.photoUrl && <meta property="og:image" content={clinic.photoUrl} />}

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={clinic.name.length <= 70 ? clinic.name : clinic.name.substring(0, 67) + '...'} />
        <meta name="twitter:description" content={(clinic.linkInBioBio || clinic.description || `Book an appointment with ${clinic.name}`).length <= 200 ? (clinic.linkInBioBio || clinic.description || `Book an appointment with ${clinic.name}`) : (clinic.linkInBioBio || clinic.description || `Book an appointment with ${clinic.name}`).substring(0, 197) + '...'} />
        {clinic.photoUrl && <meta name="twitter:image" content={clinic.photoUrl} />}
      </Helmet>

      <div className="max-w-2xl mx-auto">
        {/* Clinic Header */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-8 mb-6">
          <div className="flex items-start gap-6">
            {clinic.photoUrl && (
              <img
                src={clinic.photoUrl}
                alt={clinic.name}
                className="w-24 h-24 rounded-2xl object-cover border border-light-border dark:border-border"
              />
            )}
            <div className="flex-1">
              <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
                {clinic.name}
              </h1>
              {(clinic.linkInBioBio || clinic.description) && (
                <p className="font-body text-light-text-secondary dark:text-silver/80 leading-relaxed">
                  {clinic.linkInBioBio || clinic.description}
                </p>
              )}
              <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mt-4">
                {clinic.doctorsCount} {clinic.doctorsCount === 1 ? 'Doctor' : 'Doctors'}
              </p>
            </div>
          </div>
        </div>

        {/* Display mode: Show all doctors */}
        {showAllDoctors && shouldShowDoctors && doctors.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-display text-2xl italic tracking-tighter mb-4">
              Our Doctors
            </h2>

            {doctors.map((doctor) => (
              <button
                key={doctor.id}
                onClick={() => handleDoctorClick(doctor)}
                className="w-full glass border border-light-border dark:border-border rounded-2xl p-6 hover:border-silver/40 transition-smooth text-left group"
              >
                <div className="flex items-start gap-4">
                  {/* Doctor Photo */}
                  {doctor.photoUrl ? (
                    <img
                      src={doctor.photoUrl}
                      alt={doctor.name}
                      className="w-20 h-20 rounded-2xl object-cover border border-light-border dark:border-border"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-gradient-silver flex items-center justify-center border border-light-border dark:border-border">
                      <span className="font-display text-2xl text-obsidian">
                        {doctor.firstName?.[0]}{doctor.lastName?.[0]}
                      </span>
                    </div>
                  )}

                  {/* Doctor Info */}
                  <div className="flex-1">
                    <h3 className="font-display text-xl italic tracking-tighter mb-1 group-hover:text-pure-white transition-colors">
                      {doctor.name}
                    </h3>

                    {/* Specialties */}
                    {doctor.specialties && doctor.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {doctor.specialties.map((specialty, idx) => (
                          <span
                            key={idx}
                            className="font-mono text-xs uppercase tracking-widest px-2 py-1 bg-white/5 border border-light-border dark:border-border rounded"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Bio (truncated) */}
                    {doctor.bio && (
                      <p className="font-body text-sm text-light-text-secondary dark:text-silver/80 line-clamp-2 mb-2">
                        {doctor.bio}
                      </p>
                    )}

                    {/* Rating */}
                    {doctor.reviewCount > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                          <span className="font-mono text-sm">
                            {doctor.averageRating.toFixed(1)}
                          </span>
                        </div>
                        <span className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                          ({doctor.reviewCount} {doctor.reviewCount === 1 ? 'review' : 'reviews'})
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Arrow indicator */}
                  <div className="flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-light-text-secondary dark:text-silver/60 group-hover:text-pure-white group-hover:translate-x-1 transition-all"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* No doctors available */}
        {showAllDoctors && shouldShowDoctors && doctors.length === 0 && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-8 text-center">
            <p className="font-body text-light-text-secondary dark:text-silver/60">
              No doctors are currently available at this clinic.
            </p>
          </div>
        )}

        {/* Booking mode - show clinic booking widget */}
        {!showAllDoctors && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-8">
            <h2 className="font-display text-2xl italic tracking-tighter mb-4 text-center">
              Book an Appointment
            </h2>
            <p className="font-body text-light-text-secondary dark:text-silver/60 mb-6 text-center">
              {clinic.bookingMode === 'round_robin'
                ? 'We will automatically assign you to the next available doctor.'
                : 'Select a doctor to book an appointment.'}
            </p>
            <div className="text-center">
              <button
                onClick={() => {
                  // Redirect to first doctor's page with clinic context for round-robin
                  if (doctors.length > 0) {
                    // Pass clinicId in query to enable clinic booking mode
                    navigate(`/doctor/${doctors[0].id}?clinicId=${clinic.id}&bookingMode=${clinic.bookingMode}`);
                  }
                }}
                disabled={doctors.length === 0}
                className="px-8 py-3 bg-gradient-silver text-obsidian rounded-xl font-mono text-xs uppercase tracking-widest hover:shadow-glow transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {clinic.bookingMode === 'round_robin' ? 'Book with Auto-Assignment' : 'View Doctors & Book'}
              </button>
            </div>
          </div>
        )}

        {/* Reviews Section */}
        {shouldShowReviews && totalReviews > 0 && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-8 mt-6">
            <h2 className="font-display text-2xl italic tracking-tighter mb-4">
              Patient Reviews
            </h2>

            {/* Overall Rating */}
            <div className="flex items-center gap-6 mb-6 pb-6 border-b border-light-border dark:border-border">
              <div className="text-center">
                <div className="text-5xl font-display italic tracking-tighter mb-2">
                  {averageRating.toFixed(1)}
                </div>
                <div className="flex items-center justify-center gap-1 mb-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${
                        star <= Math.round(averageRating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-slate-300 dark:text-white/20'
                      }`}
                    />
                  ))}
                </div>
                <p className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
                  {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
                </p>
              </div>

              <div className="flex-1">
                <p className="font-body text-sm text-light-text-secondary dark:text-silver/80">
                  Overall rating across all {clinic.doctorsCount} {clinic.doctorsCount === 1 ? 'doctor' : 'doctors'} at {clinic.name}
                </p>
              </div>
            </div>

            {/* Per-Doctor Ratings (if showing all doctors) */}
            {showAllDoctors && shouldShowDoctors && (
              <div className="space-y-3">
                <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
                  Ratings by Doctor
                </h3>
                {doctors
                  .filter(doctor => doctor.reviewCount > 0)
                  .map((doctor) => (
                    <div
                      key={doctor.id}
                      className="flex items-center justify-between p-3 bg-slate-50 dark:bg-white/5 border border-light-border dark:border-border rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        {doctor.photoUrl ? (
                          <img
                            src={doctor.photoUrl}
                            alt={doctor.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-silver flex items-center justify-center">
                            <span className="font-display text-sm text-obsidian">
                              {doctor.firstName?.[0]}{doctor.lastName?.[0]}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-mono text-sm font-medium">{doctor.name}</p>
                          <p className="font-mono text-xs text-light-text-secondary dark:text-silver/60">
                            {doctor.reviewCount} {doctor.reviewCount === 1 ? 'review' : 'reviews'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-mono text-sm font-medium">
                          {doctor.averageRating.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicProfile;
